import Leg_A_Map from '/img/nwlmap/legA_map.svg?url';
import Leg_B_Map from '/img/nwlmap/legB_map.svg?url';
import Leg_C_Map from '/img/nwlmap/legC_map.svg?url';
import Leg_EW_Map from '/img/nwlmap/legEw_map.svg?url';
import Teesside_Maske_Map from '/img/nwlmap/teesside_map.svg?url';
import { capitaliseEveryWord } from '../../../utils';
import './strategycard.css';
import dayjs from 'dayjs';

const ArrowLine = () => (
	<div className="flex items-center">
		<div className="h-[8px] w-[8px] bg-gray-500 rounded-[100%]"> </div>
		<div className="h-[1px] w-full" style={{ border: '1px dashed gray' }}>
			{' '}
		</div>
		<div className="text-gray-500 mr-2">
			<svg
				xmlns="http://www.w3.org/2000/svg"
				fill="none"
				viewBox="0 0 24 24"
				strokeWidth="3"
				stroke="currentColor"
				className="size-4"
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
				/>
			</svg>
		</div>
	</div>
);

type StrategyCardProps = {
	site: {
		id: string | number;
		name: string;
		strategy?: { current?: string; recommended?: string | null };
	};
	lastUpdated: any;
	simStatus?: string | null;
	digitalTwinId?: string | number | null;
};

const statusText = (s?: StrategyCardProps['simStatus']) =>
	s === 'FAILED' || s === 'IN_PROGRESS' ? 'Data update failed' : null;

const StrategyCard = ({ site, lastUpdated, simStatus, digitalTwinId }: StrategyCardProps) => {
	const current = site.strategy?.current ?? null;
	const recommended = site.strategy?.recommended ?? null;

	const showFailureNotice = simStatus === 'FAILED' || simStatus === 'IN_PROGRESS';
	const showSmartControl = current != null;
	const showNoTwinNotice = !showFailureNotice && !digitalTwinId;

	const mapByLeg: Record<string, string> = {
		a: Leg_A_Map,
		b: Leg_B_Map,
		c: Leg_C_Map,
		ew: Leg_EW_Map,
	};

	const getMapForSiteName = (siteName: string): string | null => {
		const lower = siteName.toLowerCase();

		const legLetter = lower.match(/\bleg\s+([abc])\b/)?.[1];
		if (legLetter) return mapByLeg[legLetter] ?? null;

		if (lower.includes('leg e')) return mapByLeg.ew;
		if (lower.includes('marske')) return Teesside_Maske_Map;

		return null;
	};

	const mapSrc = getMapForSiteName(site.name);

	return (
		<div
			className={`strategy-card my-2 p-3 ${
				!showFailureNotice && !showNoTwinNotice && showSmartControl ? 'border-[#33cbef] border-l-0 border-r-0 border-t-0' : ''
			}`}
		>
			<div className="flex justify-between items-start mb-2">
				<div>
					{showFailureNotice ? (
						<h3 className="text-[24px] font-light" id={`site-${site.id}`}>
							{site.name}
						</h3>
					) : (
						<>
							<p className="font-light text-gray-700 text-[16px]">Change strategy on</p>
							<h3 className="text-[24px] font-[700]" id={`site-${site.id}`}>
								{site.name}
							</h3>
						</>
					)}
				</div>

				<div>{mapSrc && <img src={mapSrc} alt={`${site.name} map`} data-testid="location-map" />}</div>
			</div>

			<div className="pb-4">
				{showFailureNotice ? (
					<>
						<p className="font-light text-gray-700 text-[16px] pb-2">
							No strategy recommendation
						</p>
						<p className="text-[1.8rem] text-gray-700 mb-2">{statusText(simStatus)}</p>
					</>
				) : showNoTwinNotice ? (
					<>
						<p className="font-light text-[16px] pb-2">
							No strategy recommendation
						</p>
						<p className="text-[28px] font-[400] pb-3">
							There is currently no digital twin configured for this location.
						</p>
					</>
				) : !showSmartControl ? (
					<>
						<p className="font-light text-[16px] pb-2">
							No strategy recommendation
						</p>
						<p className="text-[28px] font-[400] pb-3">
							No smart control configured for this location.
						</p>
					</>
				) : (
					<div className="grid grid-cols-3 grid-rows-2 gap-2 pb-2">
						<div className="self-end">
							<span className="text-[16px] font-light">from</span>
						</div>
						<div className="self-end"></div>
						<div className="self-end">
							<span className="text-[16px] font-light">to</span>
						</div>

						<div className="self-center">
							<span className="text-[24px] font-[400]">{capitaliseEveryWord(current)}</span>
						</div>
						<div className="self-center">
							<ArrowLine />
						</div>
						<div className="self-center">
							<span className="text-[24px] font-[400]">{capitaliseEveryWord(recommended)}</span>
						</div>
					</div>
				)}

				{!showNoTwinNotice && lastUpdated && (
					<p className="text-gray-600 font-light text-[14px]">
						Updated {dayjs(lastUpdated).format('HH:mm ddd DD MMM')}
					</p>
				)}
			</div>

			{showFailureNotice || showNoTwinNotice || !showSmartControl ? null : (
				<div className="flex items-end">
					<div className="flex items-center">
						<p className="text-[#077898] font-[700]">Review strategy recommendation</p>
						<div className="h-8 w-8 ml-1 bg-[#077898] rounded-[100%] text-white flex items-center justify-center">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth="3"
								stroke="currentColor"
								className="size-4"
							>
								<path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
							</svg>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default StrategyCard;
